import CONSTANT, { CACHE } from '../utils/constants';
import { ifSuccessOrThrowError, getApi } from '../utils/api-response-utils';
import { PAYMODE, FLOW } from '../../../common/src/constant';
import { getKey, equalsIgnoreCase, money, isHybridOrAddNPay } from '../utils/common';
import { locale } from '../utils/i18n';

export function getSimplifiedPaymentOffers(cache) {
  const paymentOptions = cache.getData(CACHE.PAYMENT_OPTIONS, {});
  const isEmiSubventionFlow = cache.getData(CONSTANT.IS_EMISUBVENTION_ACCESS_TOKEN_FLOW);
  /* picking from cache since in case of login/logout the payment offers is not available in payment
   * options object */
  const paymentOffers = getKey(
    paymentOptions,
    'paymentOffers',
    cache.getData(CACHE.PAYMENT_OFFERS)
  );
  const toExecuteBankOffersOnSubvention = Boolean(isEmiSubventionFlow && paymentOffers.length);
  /* checking for bankoffers on execution here since in home this function is called directly first
   to check for offers */
  return getKey(paymentOptions, 'simplifiedPaymentOffers', toExecuteBankOffersOnSubvention);
}

export function isPromoAvailable(cache) {
  const { flow } = cache.getData(CACHE.MERCHANT_CONFIG) || {};
  const paymentOptions = cache.getData(CACHE.PAYMENT_OPTIONS, {});
  const isPwpFlow = getKey(paymentOptions, 'pwpEnabled');
  return (
    !(
      equalsIgnoreCase(flow, FLOW.DEFERRED) ||
      isHybridOrAddNPay(paymentOptions, cache) ||
      (isPwpFlow && getKey(paymentOptions, 'loginInfo.userLoggedIn', false))
    ) && Boolean(getSimplifiedPaymentOffers(cache))
  );
}

export function shouldValidateBankOffer(cache) {
  return getKey(getSimplifiedPaymentOffers(cache), 'validatePromo', false);
}

export function getFetchPaymentOptionsPromoCode(cache) {
  return getKey(getSimplifiedPaymentOffers(cache), 'promoCode');
}

export function getValidationErrorMessage(cache, offer) {
  return shouldValidateBankOffer(cache) &&
    (!getKey(offer, 'cashbackAmount') && !getKey(offer, 'instantDiscount'))
    ? locale('offers.validatePromoError')
    : null;
}
function _createOfferObject(cache, paymentOfferDetails, payMode, bankCode, validationErrorMessage) {
  validationErrorMessage =
    validationErrorMessage || getValidationErrorMessage(cache, paymentOfferDetails);

  return {
    totalInstantDiscount: getKey(paymentOfferDetails, 'instantDiscount', 0),
    totalCashbackAmount: getKey(paymentOfferDetails, 'cashbackAmount', 0),
    hasValidationError: Boolean(validationErrorMessage),
    offerBreakup: {
      [payMode]: {
        ...paymentOfferDetails,
        bankCode,
        validationErrorMessage
      }
    }
  };
}

function _getPreAppliedPromo(cache, payModeObj, isWalletUsed) {
  if (
    (Object.prototype.hasOwnProperty.call(payModeObj, 'isWalletOnly') || !isWalletUsed) &&
    payModeObj.paymentOfferDetails
  ) {
    const payMode = (payModeObj.paymentMode || payModeObj.type).toUpperCase();
    const paymentOfferDetails = { ...getKey(payModeObj, 'paymentOfferDetails') };
    const validationErrorMessage = getValidationErrorMessage(cache, paymentOfferDetails);
    return _createOfferObject(
      cache,
      paymentOfferDetails,
      payMode,
      getKey(payModeObj, 'issuingBank'),
      validationErrorMessage
    );
  }

  return null;
}

function _getPaymentOptionsObj(payModeObj, amount, bankCode, cardNumber, vpa, savedCardId, custId) {
  const paymentOptions = {};

  if (equalsIgnoreCase(payModeObj.type, CONSTANT.SC)) {
    paymentOptions.payMethod = getKey(payModeObj, 'cardDetails.cardType', '').toUpperCase();
    paymentOptions.savedCardId = getKey(payModeObj, 'cardDetails.cardId');
    paymentOptions.bankCode = getKey(payModeObj, 'issuingBank');
  } else {
    paymentOptions.payMethod = payModeObj.paymentMode.toUpperCase();

    if (bankCode) {
      paymentOptions.bankCode = bankCode;
    }

    if (savedCardId) {
      paymentOptions.savedCardId = savedCardId;
    }
  }

  if (amount) {
    paymentOptions.transactionAmount = money(amount);
  }

  if (cardNumber) {
    paymentOptions.cardNo = cardNumber;
  }

  if (payModeObj.vpaId) {
    paymentOptions.vpa = payModeObj.vpaId;
  }

  if (vpa) {
    paymentOptions.vpa = vpa;
  }
  if (custId) {
    paymentOptions.custId = custId;
  }

  return paymentOptions;
}

function _isDetailsNotSufficient(payModeObj, bankCode, cardNumber, vpa, savedCardId) {
  const paymentMode = payModeObj.paymentMode;
  const paymentType = payModeObj.type;

  return (
    (equalsIgnoreCase(paymentMode, PAYMODE.UPI) && !(vpa || payModeObj.vpaId)) ||
    ((equalsIgnoreCase(paymentMode, PAYMODE.CARD) ||
      equalsIgnoreCase(paymentMode, CONSTANT.CC) ||
      equalsIgnoreCase(paymentMode, CONSTANT.DC)) &&
      !cardNumber) ||
    (equalsIgnoreCase(paymentMode, CONSTANT.NB) && !bankCode) ||
    (equalsIgnoreCase(paymentMode, PAYMODE.EMI) && !(bankCode || savedCardId)) ||
    equalsIgnoreCase(paymentType, CONSTANT.MGV)
  );
}

function _isCachableOfferPaymode(payModeObj) {
  const payMode = getKey(payModeObj, 'type');
  return [PAYMODE.NB, CONSTANT.SC].some(allowedpayMode =>
    equalsIgnoreCase(allowedpayMode, payMode)
  );
}

function _getOfferCachekey(payModeObj, isWalletUsed, bankCode) {
  let key = isWalletUsed ? 'hybrid_' : '';
  key += `${getKey(payModeObj, 'type')}_${bankCode}_${getKey(payModeObj, 'cardDetails.cardId')}`;
  return key;
}

function _getCachedOffer(cache, payModeObj, isWalletUsed, bankCode) {
  if (!_isCachableOfferPaymode(payModeObj)) {
    return null;
  }

  const cacheKey = _getOfferCachekey(payModeObj, isWalletUsed, bankCode);
  return cache.getData(CACHE.BANK_OFFERS, {})[cacheKey];
}

function _setOfferCache(cache, payModeObj, isWalletUsed, bankCode, value) {
  if (!_isCachableOfferPaymode(payModeObj)) {
    return;
  }
  const cacheKey = _getOfferCachekey(payModeObj, isWalletUsed, bankCode);
  const cached = cache.getData(CACHE.BANK_OFFERS, {});

  cache.setData(CACHE.BANK_OFFERS, {
    ...cached,
    [cacheKey]: { ...value }
  });
}
function _isCardPayMode(payMethod) {
  return equalsIgnoreCase(payMethod, CONSTANT.CC) || equalsIgnoreCase(payMethod, CONSTANT.DC);
}

export function handleApplyPromoError(cache, error, payMethod, bankCode, isSC = false) {
  if (shouldValidateBankOffer(cache)) {
    const errorMessage = getValidationErrorMessage(cache);
    payMethod = _isCardPayMode(payMethod) ? (isSC ? CONSTANT.SC : PAYMODE.CARD) : payMethod;

    return _createOfferObject(cache, {}, payMethod, bankCode, errorMessage);
  }

  return null;
}

export function parseApplyPromoResponse(cache, isSC, bankCode, response) {
  if ((getKey(response, 'paymentOffer.offerBreakup') || []).length) {
    const paymentOffer = { ...response.paymentOffer };
    let hasValidationError = false;

    paymentOffer.offerBreakup = paymentOffer.offerBreakup.reduce((acc, cur) => {
      // card paymethod name renaming
      const payMethod = _isCardPayMode(cur.payMethod)
        ? isSC
          ? CONSTANT.SC
          : PAYMODE.CARD
        : cur.payMethod.toUpperCase();
      const validationErrorMessage = getValidationErrorMessage(cache, cur);

      if (validationErrorMessage) {
        hasValidationError = true;
      }

      acc[payMethod] = {
        ...cur,
        bankCode: equalsIgnoreCase(cur.payMethod, PAYMODE.WALLET) ? '' : bankCode,
        validationErrorMessage
      };
      return acc;
    }, {});

    paymentOffer.hasValidationError = hasValidationError;

    return paymentOffer;
  }

  return null;
}

export function getApplyPromoBaseRequestObj(cache, custId = '') {
  const wallet = getKey(cache.getData(CACHE.PAYMENT_OPTIONS), 'wallet');
  const isWalletUsed = getKey(wallet, 'isUsed', false);
  const txnAmount = Number(cache.getData(CONSTANT.UNMODIFIED_TXN_AMOUNT, 0));

  const requestObj = {
    custId,
    totalTransactionAmount: money(txnAmount),
    paymentOptions: []
  };

  const promocode = getFetchPaymentOptionsPromoCode(cache);
  if (promocode) {
    requestObj.promocode = promocode;
  }

  if (isWalletUsed) {
    const walletBallence = Number(getKey(wallet, 'walletBalance', 0));
    const amount = walletBallence >= txnAmount ? txnAmount : walletBallence;

    requestObj.paymentOptions.push(_getPaymentOptionsObj({ paymentMode: CONSTANT.WALLET }, amount));
  }

  return requestObj;
}

export function applyPromo(cache, payModeObj, bankCode, cardNumber, vpa, savedCardId, custId) {
  if (!payModeObj || equalsIgnoreCase(payModeObj.paymentMode, PAYMODE.LOGIN)) {
    return Promise.reject(new Error());
  }
  let isWalletPayMode = false;
  if (
    payModeObj &&
    Object.prototype.hasOwnProperty.call(payModeObj, 'isWalletOnly') &&
    !payModeObj.used
  ) {
    isWalletPayMode = true;
    return Promise.reject(new Error());
  }

  const isWalletUsed = getKey(cache.getData(CACHE.PAYMENT_OPTIONS), 'wallet.isUsed', false);
  const preAppliedPromo = _getPreAppliedPromo(cache, payModeObj, isWalletUsed);
  if (preAppliedPromo) {
    return Promise.resolve(preAppliedPromo);
  }

  if (_isDetailsNotSufficient(payModeObj, bankCode, cardNumber, vpa, savedCardId)) {
    return Promise.reject();
  }

  const cachedOffer = _getCachedOffer(cache, payModeObj, isWalletUsed, bankCode);
  if (cachedOffer) {
    return Promise.resolve(cachedOffer);
  }

  const requestObj = getApplyPromoBaseRequestObj(cache, custId);
  if (!isWalletPayMode) {
    const nonWalletPaymodeAmount = requestObj.paymentOptions.length
      ? Number(requestObj.totalTransactionAmount) -
        Number(requestObj.paymentOptions[0].transactionAmount)
      : Number(requestObj.totalTransactionAmount);

    requestObj.paymentOptions.push(
      _getPaymentOptionsObj(
        payModeObj,
        nonWalletPaymodeAmount,
        bankCode,
        cardNumber,
        vpa,
        savedCardId
      )
    );
  }

  return getApi(cache)
    .applyPromo(requestObj)
    .then(response =>
      ifSuccessOrThrowError(response, callBackResponse => {
        const parsedResponse = parseApplyPromoResponse(
          cache,
          equalsIgnoreCase(payModeObj.type, CONSTANT.SC),
          bankCode,
          callBackResponse
        );
        _setOfferCache(cache, payModeObj, isWalletUsed, bankCode, parsedResponse);
        return parsedResponse;
      })
    )
    .catch(error => {
      const payMethod = isWalletPayMode
        ? PAYMODE.WALLET
        : (payModeObj.paymentMode || payModeObj.type).toUpperCase();
      return handleApplyPromoError(
        cache,
        error,
        payMethod,
        bankCode,
        equalsIgnoreCase(payModeObj.type, CONSTANT.SC)
      );
    });
}



// WEBPACK FOOTER //
// ./controllers/offers.js